// The one shared renderer for HTML email bodies (v1.5.54) — sanitizes, injects, and makes
// images click-to-lightbox. Inline (cid:) images arrive as data: URIs (the server resolves
// them in GET /api/emails/message), external ones as https: — both open in the same
// fullscreen overlay, mirroring the TaskDetailRow/chat lightbox pattern (the app deliberately
// moved image viewing from new tabs to a lightbox — see Feedback.tsx's changelog).

import { useState } from "react";
import { sanitizeEmailHtml } from "@/lib/emailHtml";

interface EmailHtmlBodyProps {
  html: string;
  className?: string;
}

const OPENABLE_SRC = /^(data:image\/|https?:|blob:)/i;

export function EmailHtmlBody({ html, className }: EmailHtmlBodyProps) {
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.tagName !== "IMG") return;
    // An image wrapped in a real link (signature logo → website) keeps its link behavior.
    if (target.closest("a[href]")) return;
    const src = target.getAttribute("src") ?? "";
    if (!OPENABLE_SRC.test(src)) return; // unresolved cid:, empty, etc. — nothing to show
    e.preventDefault();
    setLightboxSrc(src);
  };

  // ENG-105: the body box is a fixed light "paper" surface in EVERY theme. Most email HTML
  // hardcodes dark text (Outlook color:black, <font color>), so a themed dark background makes
  // it unreadable. Don't swap this for bg-background, and don't try to recolor the text to the
  // theme instead - that needs !important on every descendant and breaks emails that set their
  // own light backgrounds. (prose/dark:prose-invert are no-ops: @tailwindcss/typography isn't installed.)
  return (
    <>
      <div
        className={`${className ?? ""} bg-white text-neutral-900 [color-scheme:light] [&_img]:cursor-zoom-in`}
        onClick={handleClick}
        dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(html) }}
      />
      {lightboxSrc && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={() => setLightboxSrc(null)}
          onKeyDown={(e) => e.key === "Escape" && setLightboxSrc(null)}
          tabIndex={-1}
        >
          <img
            src={lightboxSrc}
            alt="Email image"
            className="max-w-[90vw] max-h-[90vh] rounded shadow-xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
